import React from 'react';
import styled from '@emotion/styled';
import type { StayFull } from '../../../../interfaces/stays';
import { StatusPill } from '../../../status-pill/status-pill';
import { BodyRow, Td } from '../../../table/list-table';
import { formatDayKey } from '../../../../function-utils/building-time';
import { palette, spacing, typography } from '../../../../style-utils/theme';
import { shortName } from './reservation-labels';

/**
 * THE SAME-DAY TURNAROUND BRIDGE (plan S4): one row between a departure and
 * the arrival on the same building day, saying how long the cleaners have.
 * Both times are BUILDING wall times (data-time); the window is the server's
 * (`turnaround.window_hours`), never recomputed here.
 */

const Bridge = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
`;

const Strong = styled.span`
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Times = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
`;

/** "4 h to clean" / "1.5 h to clean" / "Immediate turnaround". */
export const turnaroundPill = (hours: number): { tone: 'warning' | 'neutral'; text: string } => {
  const h = Math.max(0, Math.round(hours * 10) / 10);
  if (h === 0) return { tone: 'warning', text: 'Immediate turnaround' };

  return { tone: h <= 2 ? 'warning' : 'neutral', text: `${h} h to clean` };
};

export interface TurnaroundRowProps {
  departing: StayFull;
  arriving: StayFull;
  date: string;
  hours: number;
  /** The table's column count, for the one spanning cell. */
  columns: number;
  index: number;
}

export const TurnaroundRow: React.FC<TurnaroundRowProps> = ({ departing, arriving, date, hours, columns, index }) => {
  const pill = turnaroundPill(hours);

  return (
    <BodyRow index={index} data-turnaround={date} data-out={departing.id} data-in={arriving.id}>
      <Td colSpan={columns} data-label="Turnaround">
        <Bridge>
          <Strong>Same-day turnaround · {formatDayKey(date)}</Strong>
          <Times>
            {shortName(departing)} leaves at <span data-time={departing.effective.departure_time}>{departing.effective.departure_time}</span>
            {' → '}
            {shortName(arriving)} arrives at <span data-time={arriving.effective.arrival_time}>{arriving.effective.arrival_time}</span>
          </Times>
          <StatusPill tone={pill.tone} dot>
            {pill.text}
          </StatusPill>
        </Bridge>
      </Td>
    </BodyRow>
  );
};
